import { useState, useContext, useEffect, useRef } from 'react';
import './Asthma.scss';
import { GenericAccordion } from './components/GenericAccordion';
import AsthmaCalculator from './components/AsthmaCalculator';
import { MildSectionKey, ActiveView, AssessmentData } from './utils/asthmaTypes';
import { getMaxDosesForSeverity, calculateAsthmaScore, mapScoreToSeverity } from './utils/asthmaScoring';
import { useAsthmaFlow } from './hooks/useAsthmaFlow';
import { useAsthmaAssessment } from './hooks/useAsthmaAssessment';
import { getNextStep } from './asthmaFlowLogic';
import { AsthmaPopups } from './components/popups/AsthmaPopups';
import { DepartmentMember } from './components/popups/EmailSummaryPopup';

import { AsthmaResultsHeader } from './components/AsthmaResultsHeader';
import { useAsthmaAccordionItems } from './hooks/useAsthmaAccordionItems';
import { useAsthmaStorage } from './hooks/useAsthmaStorage';
import { UserContext } from '../../context/UserContext';
import './AsthmaFlow.scss';
import API from '../../config/axiosConfig';
import { AssessmentHistoryModal } from './components/AssessmentHistoryModal';
import AppsHeader from "../components/AppsHeader";
import React from 'react';

const AsthmaSeverity = () => {
  const [activeView, setActiveView] = useState<ActiveView>('CALCULATOR');
  const [recheckRound, setRecheckRound] = useState<number | null>(null);

  // 1. State to save "Snapshot" of initial data
  const [initialData, setInitialData] = useState<AssessmentData | null>(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [modalTopOffset, setModalTopOffset] = useState(0);
  const resultsHeaderRef = useRef<HTMLDivElement>(null);
  const [improvedFromSevere, setImprovedFromSevere] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const [showIncompletePopup, setShowIncompletePopup] = useState(false);
  const [pendingRecheckTarget, setPendingRecheckTarget] = useState<MildSectionKey | null>(null);
  const [showAllergyPopup, setShowAllergyPopup] = useState(false);
  const [showSteroidPopup, setShowSteroidPopup] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [departmentMembers, setDepartmentMembers] = useState<DepartmentMember[]>([]);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showPrematureClosePopup, setShowPrematureClosePopup] = useState(false);
  const [showLoginRequiredPopup, setShowLoginRequiredPopup] = useState(false);

  const { user } = useContext(UserContext) as any;

  // Fetch department members when email popup opens
  useEffect(() => {
    if (!showEmailPopup || !user?.roles?.length) return;
    const { hospital: hospitalId, department: departmentId } = user.roles[0];
    if (!hospitalId || !departmentId) return;

    API.get(`/api/users/department-members?hospital=${hospitalId}&department=${departmentId}`)
      .then((res) => {
        const members: DepartmentMember[] = res.data;
        setDepartmentMembers(members);
      })
      .catch(() => setDepartmentMembers([]));
  }, [showEmailPopup, user]);

  const assessment = useAsthmaAssessment();
  const flow = useAsthmaFlow();

  const { clearStorage, isLoaded, checkExpiration } = useAsthmaStorage(
    {
      setAge: assessment.setAge,
      setRespiratoryRate: assessment.setRespiratoryRate,
      setOxygenSaturation: assessment.setOxygenSaturation,
      setOxygenSupport: assessment.setOxygenSupport,
      setAuscultation: assessment.setAuscultation,
      setRetractions: assessment.setRetractions,
      setDyspnea: assessment.setDyspnea,
      setInitialAssessmentTime: assessment.setInitialAssessmentTime,

      setScore: assessment.setScore,
      setActiveSeverity: assessment.setActiveSeverity,
      setSeverity: assessment.setSeverity,
      setTreatmentRecommendation: assessment.setTreatmentRecommendation,

      setIsAssessmentLocked: assessment.setIsAssessmentLocked,

      setMedicationLog: flow.setMedicationLog,
      setCurrentMedications: flow.setCurrentMedications,
      setSecondRoundStarted: flow.setSecondRoundStarted,
      setThirdRoundStarted: flow.setThirdRoundStarted,
      setRecheckDone: flow.setRecheckDone,
      setDosesAtStartOfRound2: flow.setDosesAtStartOfRound2,
      setDosesAtStartOfRound3: flow.setDosesAtStartOfRound3,
      setOpenSection: flow.setOpenSection,
      setRecheckResults: flow.setRecheckResults,
      setInitialData: setInitialData,

      setActiveView,
      setImprovedFromSevere,
      setIsEmailSent
    },
    {
      age: assessment.age,
      respiratoryRate: assessment.respiratoryRate,
      oxygenSaturation: assessment.oxygenSaturation,
      oxygenSupport: assessment.oxygenSupport,
      auscultation: assessment.auscultation,
      retractions: assessment.retractions,
      dyspnea: assessment.dyspnea,
      initialAssessmentTime: assessment.initialAssessmentTime,

      score: assessment.score,
      activeSeverity: assessment.activeSeverity,
      severity: assessment.severity,
      treatmentRecommendation: assessment.treatmentRecommendation,

      isAssessmentLocked: assessment.isAssessmentLocked,

      medicationLog: flow.medicationLog,
      currentMedications: flow.currentMedications,
      secondRoundStarted: flow.secondRoundStarted,
      thirdRoundStarted: flow.thirdRoundStarted,
      recheckDone: flow.recheckDone,
      dosesAtStartOfRound2: flow.dosesAtStartOfRound2,
      dosesAtStartOfRound3: flow.dosesAtStartOfRound3,
      openSection: flow.openSection,
      recheckResults: flow.recheckResults,
      initialData: initialData,

      activeView,
      improvedFromSevere,
      isEmailSent
    }
  );

  const handleReset = () => {
    clearStorage();
    setRecheckRound(null);
    setInitialData(null);

    assessment.setAge('');
    assessment.setRespiratoryRate('');
    assessment.setOxygenSaturation('');
    assessment.setOxygenSupport(false);
    assessment.setAuscultation('');
    assessment.setRetractions('');
    assessment.setDyspnea('');
    assessment.setScore(0);
    assessment.setActiveSeverity(null);
    assessment.setSeverity('');
    assessment.setTreatmentRecommendation('');

    assessment.setIsAssessmentLocked(false);

    flow.setMedicationLog([]);
    flow.setSecondRoundStarted(false);
    flow.setThirdRoundStarted(false);
    flow.setRecheckDone(false);
    flow.setDosesAtStartOfRound2(0);
    flow.setDosesAtStartOfRound3(0);
    flow.setOpenSection('INITIAL');
    flow.setRecheckResults({});

    setIsEmailSent(false);
    setActiveView('CALCULATOR');
    setImprovedFromSevere(false);
  };

  // Listen for visibility changes (returning to tab) to check expiration
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const isExpired = checkExpiration();
        if (isExpired) {
          handleReset();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkExpiration, handleReset]);

  const handleContinue = () => {
    if (!assessment.activeSeverity) return;
    assessment.stampInitialAssessmentTime();
    assessment.setIsAssessmentLocked(true);

    // 2. Save the Snapshot of initial data
    const snapshot: AssessmentData = {
      age: assessment.age,
      respiratoryRate: assessment.respiratoryRate,
      oxygenSaturation: assessment.oxygenSaturation,
      oxygenSupport: assessment.oxygenSupport,
      auscultation: assessment.auscultation,
      retractions: assessment.retractions,
      dyspnea: assessment.dyspnea,
      score: assessment.score,
      severityLabel: assessment.severity, // Note: here we take from the Hook
      activeSeverity: assessment.activeSeverity,
      timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
    };
    setInitialData(snapshot);

    setActiveView('FLOW');
    flow.setOpenSection('TREATMENT');
  };

  const handleStartRecheck = (round: number) => {
    assessment.setRespiratoryRate('');
    assessment.setOxygenSaturation('');
    assessment.setOxygenSupport(false);
    assessment.setAuscultation('');
    assessment.setRetractions('');
    assessment.setDyspnea('');

    setRecheckRound(round);
  };

  // Function to cancel recheck and return to accordion
  const handleCancelRecheck = () => {
    setRecheckRound(null);
  };

  const handleRecheckCalculatorSubmit = () => {
    if (recheckRound === null) return;

    const newScore = calculateAsthmaScore({
      age: assessment.age,
      respiratoryRate: assessment.respiratoryRate,
      oxygenSaturation: assessment.oxygenSaturation,
      oxygenSupport: assessment.oxygenSupport,
      auscultation: assessment.auscultation,
      retractions: assessment.retractions,
      dyspnea: assessment.dyspnea
    });

    const { severityLabel, severityLevel: activeSeverity } = mapScoreToSeverity(newScore);

    const recheckData: AssessmentData = {
      age: assessment.age,
      respiratoryRate: assessment.respiratoryRate,
      oxygenSaturation: assessment.oxygenSaturation,
      oxygenSupport: assessment.oxygenSupport,
      auscultation: assessment.auscultation,
      retractions: assessment.retractions,
      dyspnea: assessment.dyspnea,
      score: newScore,
      severityLabel,
      activeSeverity,
      timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
    };

    handleRecheckDone(recheckRound, recheckData);
    setRecheckRound(null);
  };

  const handleSendEmailFinal = async (recipientId: string | null, patientName: string) => {
    try {
      await API.post('/api/mail/send-from-app', {
        recipientId,
        patientName: patientName || undefined,
        initialAssessment: initialData,
        medicationLog: flow.medicationLog,
        recheckResults: flow.recheckResults,
        dosesAtStartOfRound2: flow.dosesAtStartOfRound2,
        dosesAtStartOfRound3: flow.dosesAtStartOfRound3,
        finalScore: assessment.score,
        finalSeverity: assessment.severity,
      });

      setIsEmailSent(true);
      setShowEmailPopup(false);
      alert('הסיכום נשלח בהצלחה!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('אירעה שגיאה בשליחת המייל.');
    }
  };

  const handleToggleSection = (targetSection: MildSectionKey) => {
    if (flow.openSection === targetSection) {
      flow.setOpenSection(null);
      return;
    }
    if (targetSection === 'RECHECK' && flow.openSection === 'TREATMENT') {
      const maxDoses = getMaxDosesForSeverity(assessment.activeSeverity, 1);
      if (flow.medicationLog.length < maxDoses) {
        setPendingRecheckTarget('RECHECK');
        setShowIncompletePopup(true);
        return;
      }
    }
    if (targetSection === 'RECHECK_2' && flow.openSection === 'TREATMENT_2') {
      let maxDoses = getMaxDosesForSeverity(assessment.activeSeverity, 2);
      if (assessment.activeSeverity === 'SEVERE' && flow.dosesAtStartOfRound2 === 3) maxDoses = 4;
      if (flow.medicationLog.length < maxDoses) {
        setPendingRecheckTarget('RECHECK_2');
        setShowIncompletePopup(true);
        return;
      }
    }
    if (targetSection === 'RECHECK_3' && flow.openSection === 'TREATMENT_3') {
      const maxDoses = getMaxDosesForSeverity(assessment.activeSeverity, 3);
      if (flow.medicationLog.length < maxDoses) {
        setPendingRecheckTarget('RECHECK_3');
        setShowIncompletePopup(true);
        return;
      }
    }
    flow.setOpenSection(targetSection);
  };

  const handleRecheckDone = (round: number, data: AssessmentData) => {
    const { score: newScore, activeSeverity: newSeverity, severityLabel } = data;
    const prevSeverity = assessment.activeSeverity;

    flow.saveRecheck(round, data);

    assessment.setScore(newScore);
    assessment.setActiveSeverity(newSeverity);
    assessment.setSeverity(severityLabel || '');

    if (!newSeverity) {
      flow.setRecheckDone(true);
      return;
    }

    let simulatedCurrentSection: MildSectionKey = 'RECHECK';
    if (round === 2) simulatedCurrentSection = 'RECHECK_2';
    if (round === 3) simulatedCurrentSection = 'RECHECK_3';

    const nextState = getNextStep({
      currentSection: simulatedCurrentSection,
      prevSeverity,
      newSeverity,
      secondRoundStarted: flow.secondRoundStarted,
      thirdRoundStarted: flow.thirdRoundStarted
    });

    if (prevSeverity === 'SEVERE' && (newSeverity === 'MILD' || newSeverity === 'MODERATE')) {
      setImprovedFromSevere(true);
    }
    if (nextState.nextSecondRoundStarted && !flow.secondRoundStarted) {
      flow.setDosesAtStartOfRound2(flow.medicationLog.length);
    }
    if (newSeverity === 'MILD') setShowSteroidPopup(true);
    if (prevSeverity === 'MODERATE' && newSeverity === 'SEVERE') setShowAllergyPopup(true);

    flow.setOpenSection(nextState.nextSection);
    flow.setSecondRoundStarted(nextState.nextSecondRoundStarted);
    flow.setThirdRoundStarted(nextState.nextThirdRoundStarted);
    flow.setRecheckDone(nextState.nextRecheckDone);
  };

  const accordionItems = useAsthmaAccordionItems({
    assessment,
    flow,
    improvedFromSevere,
    setImprovedFromSevere,
    isEmailSent,
    isLoggedIn: !!user,
    handleToggleSection,
    handleRecheckDone,
    handleReset,
    handleStartRecheck,
    onSendEmailClick: () => {
      if (!user) {
        setShowLoginRequiredPopup(true);
      } else {
        setShowEmailPopup(true);
      }
    },
    setShowCloseConfirmation,
    setShowPrematureClosePopup,
    // Now the Hook knows to receive this data
    initialData
  });

  if (!isLoaded) return null;

  const showCalculator = activeView === 'CALCULATOR' || recheckRound !== null;
  const showFlow = activeView === 'FLOW' && recheckRound === null;

  // === Critical fix for the model ===
  // Preparing data for the model explicitly by source (Snapshot or Hook)
  // To prevent type errors

  let preparedModalData;

  if (initialData) {
    // Case 1: Using stored data (AssessmentData)
    preparedModalData = {
      age: initialData.age,
      respiratoryRate: initialData.respiratoryRate,
      oxygenSaturation: initialData.oxygenSaturation,
      oxygenSupport: initialData.oxygenSupport,
      auscultation: initialData.auscultation,
      retractions: initialData.retractions,
      dyspnea: initialData.dyspnea,
      score: initialData.score,
      severityLabel: initialData.severityLabel, // In Snapshot it is severityLabel
      activeSeverity: initialData.activeSeverity,
      initialAssessmentTime: initialData.timestamp // In Snapshot it is timestamp
    };
  } else {
    // Case 2: Using live data (fallback)
    preparedModalData = {
      age: assessment.age,
      respiratoryRate: assessment.respiratoryRate,
      oxygenSaturation: assessment.oxygenSaturation,
      oxygenSupport: assessment.oxygenSupport,
      auscultation: assessment.auscultation,
      retractions: assessment.retractions,
      dyspnea: assessment.dyspnea,
      score: assessment.score,
      severityLabel: assessment.severity, // In Hook it is severity
      activeSeverity: assessment.activeSeverity,
      initialAssessmentTime: assessment.initialAssessmentTime || ''
    };
  }

  return (
    <div className="asthma-wrapper" dir="rtl">
      <div className="container main-content asthma-calculator-container">

        {showCalculator && (
          <>
            <AppsHeader title="טיפול באסתמה" onReset={handleReset} />

            {recheckRound && (
              <div style={{ marginBottom: '1.5rem' }}>
                <AsthmaResultsHeader
                  score={assessment.score}
                  severity={assessment.severity}
                  onClick={() => setShowHistoryModal(true)}
                  round={(recheckRound || 0) + 1}
                />
              </div>
            )}

            <AsthmaCalculator
              age={assessment.age} setAge={assessment.setAge}
              respiratoryRate={assessment.respiratoryRate} setRespiratoryRate={assessment.setRespiratoryRate}
              oxygenSaturation={assessment.oxygenSaturation} setOxygenSaturation={assessment.setOxygenSaturation}
              oxygenSupport={assessment.oxygenSupport} setOxygenSupport={assessment.setOxygenSupport}
              auscultation={assessment.auscultation} setAuscultation={assessment.setAuscultation}
              retractions={assessment.retractions} setRetractions={assessment.setRetractions}
              dyspnea={assessment.dyspnea} setDyspnea={assessment.setDyspnea}
              onContinue={recheckRound ? handleRecheckCalculatorSubmit : handleContinue}
              onReset={handleReset}
              onBack={recheckRound ? handleCancelRecheck : undefined}
              hideAge={recheckRound !== null}
            />
          </>
        )}

        {showFlow && (
          <>
            <AppsHeader title="טיפול באסתמה" onReset={handleReset} />
            <div className="asthma-calculator-inner-container" dir="rtl">
              <div className="asthma-calculator-grid">
                <div className="asthma-calculator-results-column">
                  <div ref={resultsHeaderRef}>
                    <AsthmaResultsHeader
                      score={assessment.score}
                      severity={assessment.severity}
                      onClick={() => {
                        if (resultsHeaderRef.current) {
                          setModalTopOffset(resultsHeaderRef.current.getBoundingClientRect().bottom);
                        }
                        setShowHistoryModal(true);
                      }}
                      round={flow.recheckResults[2] ? 3 : (flow.recheckResults[1] ? 2 : undefined)}
                    />
                  </div>
                  <GenericAccordion items={accordionItems} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <AsthmaPopups
        showEmailPopup={showEmailPopup} setShowEmailPopup={setShowEmailPopup}
        showIncompletePopup={showIncompletePopup} setShowIncompletePopup={setShowIncompletePopup}
        showAllergyPopup={showAllergyPopup} setShowAllergyPopup={setShowAllergyPopup}
        showSteroidPopup={showSteroidPopup} setShowSteroidPopup={setShowSteroidPopup}
        showCloseConfirmation={showCloseConfirmation} setShowCloseConfirmation={setShowCloseConfirmation}
        showPrematureClosePopup={showPrematureClosePopup} setShowPrematureClosePopup={setShowPrematureClosePopup}
        showLoginRequiredPopup={showLoginRequiredPopup} setShowLoginRequiredPopup={setShowLoginRequiredPopup}
        isEmailSent={isEmailSent}
        pendingRecheckTarget={pendingRecheckTarget}
        defaultEmail={user?.email || ''}
        departmentMembers={departmentMembers}
        onOpenSection={flow.setOpenSection}
        onSendEmail={handleSendEmailFinal}
        onRequestEmail={() => {
          if (!user) {
            setShowLoginRequiredPopup(true);
          } else {
            setShowEmailPopup(true);
          }
        }}
        onReset={handleReset}
        onSkipToRecheck={handleStartRecheck}
      />

      <AssessmentHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        topOffset={modalTopOffset}
        data={preparedModalData}
      />
    </div>
  );
};

export default AsthmaSeverity;